import { User } from 'firebase/auth';
import * as E from 'fp-ts/Eq';
import {
  Case,
  CaseStatus,
  LastMessageStatus,
  Site,
} from '../services/buildfire/rdb/cases';
import moment from 'moment-timezone';

export const caseIsEqual: E.Eq<Case> = {
  equals: (x, y) => x.id === y.id,
};

export function doesCaseHaveUnreadMessage(
  c: Case,
  currentUser: User,
  isAdmin: boolean
) {
  if (!c.lastMessage || c.lastMessageStatus !== LastMessageStatus.UNREAD) {
    return false;
  }

  if (isAdmin) {
    return c.lastMessage.uid === c.uid;
  }
  return c.lastMessage.uid !== currentUser?.uid;
}

export function formatCaseNumber(num: number = 0) {
  return String(num).padStart(4, '0');
}

export function isOpen(c: Case) {
  return c.status === CaseStatus.OPEN;
}

const ADMIN_TIMEZONE = 'America/Chicago';

const getNextBusinessDay = (
  startTime: moment.Moment,
  businessStart: string,
  businessEnd: string,
  hoursToAdd: number = 0,
  returnTime: 'start' | 'end' | 'submission' = 'submission'
) => {
  let nextBusinessDay = startTime.clone();
  const dayOfWeek = nextBusinessDay.isoWeekday();

  if (hoursToAdd > 0) {
    if (dayOfWeek >= 6) {
      nextBusinessDay.isoWeekday(8);
    } else {
      nextBusinessDay.add(1, 'day');
      if (nextBusinessDay.isoWeekday() >= 6) {
        nextBusinessDay.isoWeekday(8);
      }
    }
    const businessStartTime = moment.tz(
      `${nextBusinessDay.format('YYYY-MM-DD')} ${businessStart}`,
      'YYYY-MM-DD HH:mm',
      ADMIN_TIMEZONE
    );
    return businessStartTime.add(hoursToAdd, 'hours');
  }

  if (returnTime === 'end') {
    nextBusinessDay.add(1, 'day');
    if (nextBusinessDay.isoWeekday() >= 6) {
      nextBusinessDay.isoWeekday(8);
    }
    return moment.tz(
      `${nextBusinessDay.format('YYYY-MM-DD')} ${businessEnd}`,
      'YYYY-MM-DD HH:mm',
      ADMIN_TIMEZONE
    );
  }

  nextBusinessDay.add(1, 'day');
  if (nextBusinessDay.isoWeekday() >= 6) {
    nextBusinessDay.isoWeekday(8);
  }

  return nextBusinessDay;
};

/**
 * Rules for calculating due by time:
 * - Priority cases: 2 hours turnaround
 *   - Submitted during business hours (CT): add 2 hours
 *   - Submitted after business hours or on weekend: due by opening of next day + 2 hours (whether weekend or weekday)
 * - Standard cases: 1 business day turnaround
 *   - Submitted during business hours (CT) on a weekday: due by submission time next business day
 *   - Submitted before business hours Monday through Friday: due 24 hours from submission time or Friday at close of business, whichever is earlier
 *   - Submitted after business hours Monday through Thursday: due 24 hours from submission time or Friday at close of business, whichever is earlier
 *   - Submitted from business close on Friday through Sunday: due by end of business hours on Monday
 *   - Submitted Friday during business hours: due by same time of day on Monday
 *
 * Business hours are defined by businessStart and businessEnd parameters (in HH:mm format, CT timezone)
 *
 * Examples:
 * - Thursday 3pm CT priority submission should be due Thursday 5pm CT
 * - Friday 6pm CT priority submission (after hours) should be due Saturday 10am CT
 * - Saturday 2pm CT priority submission should be due Sunday 10am CT
 * - Sunday 3pm CT standard submission should be due Monday 4pm CT (closing time)
 * - Monday 7am CT standard submission (before hours) should be due Tuesday 7am CT
 * - Monday 10am CT standard submission should be due Tuesday 10am CT
 * - Thursday 6pm CT standard submission (after hours) should be due Friday 4pm CT (24h would be Fri 6pm, capped at close)
 * - Thursday 11pm CT standard submission (after hours) should be due Friday 4pm CT (24h would be Fri 11pm, capped at close)
 * - Friday 7am CT standard submission (before hours) should be due Friday 4pm CT (24h would be Sat 7am, capped at close)
 * - Friday 3pm CT standard submission should be due Monday 3pm CT
 *
 * Returns dueBy moment object in CT timezone, along with booleans for isAfterHours and isBeforeHours
 */
export function dueBy(
  businessStart: string,
  businessEnd: string,
  isPriority: boolean,
  createdAt: number | string
): {
  dueBy: moment.Moment;
  isAfterHours: boolean;
  isBeforeHours: boolean;
} {
  const submissionTime = moment.tz(createdAt, 'UTC').tz(ADMIN_TIMEZONE);
  // Business end time on the day of case submission
  const afterHoursStart = moment.tz(
    `${submissionTime.format('YYYY-MM-DD')} ${businessEnd}`,
    'YYYY-MM-DD HH:mm',
    ADMIN_TIMEZONE
  );
  const beforeHoursStart = moment.tz(
    `${submissionTime.format('YYYY-MM-DD')} ${businessStart}`,
    'YYYY-MM-DD HH:mm',
    ADMIN_TIMEZONE
  );
  const isAfterHours = submissionTime.isAfter(afterHoursStart);
  const isBeforeHours = submissionTime.isBefore(beforeHoursStart);
  const isWeekend = submissionTime.isoWeekday() >= 6;

  if (isPriority) {
    if (isAfterHours || isWeekend) {
      const nextDay = submissionTime.clone().add(1, 'day');
      const dueBy = moment.tz(
        `${nextDay.format('YYYY-MM-DD')} ${businessStart}`,
        'YYYY-MM-DD HH:mm',
        ADMIN_TIMEZONE
      ).add(2, 'hours');

      return {
        dueBy,
        isAfterHours,
        isBeforeHours,
      };
    }

    if (isBeforeHours) {
      return {
        dueBy: moment
          .tz(
            `${submissionTime.format('YYYY-MM-DD')} ${businessStart}`,
            'YYYY-MM-DD HH:mm',
            ADMIN_TIMEZONE
          )
          .add(2, 'hours'),
        isAfterHours,
        isBeforeHours,
      };
    }

    return {
      dueBy: moment.tz(
        `${submissionTime.format('YYYY-MM-DD')} ${submissionTime
          .clone()
          .add(2, 'hours')
          .format('HH:mm')}`,
        'YYYY-MM-DD HH:mm',
        ADMIN_TIMEZONE
      ),
      isAfterHours,
      isBeforeHours,
    };
  }

  if (isWeekend) {
    const dueBy = getNextBusinessDay(
      submissionTime,
      businessStart,
      businessEnd,
      0,
      'end'
    );
    return { dueBy, isAfterHours, isBeforeHours };
  }

  if (isBeforeHours) {
    const twentyFourHoursLater = submissionTime.clone().add(24, 'hours');
    const fridayOfWeek = submissionTime.clone().isoWeekday(5);
    const fridayClose = moment.tz(
      `${fridayOfWeek.format('YYYY-MM-DD')} ${businessEnd}`,
      'YYYY-MM-DD HH:mm',
      ADMIN_TIMEZONE
    );

    return {
      dueBy: moment.min(twentyFourHoursLater, fridayClose),
      isAfterHours,
      isBeforeHours,
    };
  }

  if (isAfterHours) {
    const dayOfWeek = submissionTime.isoWeekday();

    if (dayOfWeek === 5) {
      // Friday after hours -> Monday close
      const dueBy = getNextBusinessDay(
        submissionTime,
        businessStart,
        businessEnd,
        0,
        'end'
      );
      return { dueBy, isAfterHours, isBeforeHours };
    }

    // Monday-Thursday after hours: add 24h, cap at Friday close
    const twentyFourHoursLater = submissionTime.clone().add(24, 'hours');
    const fridayOfWeek = submissionTime.clone().isoWeekday(5);
    const fridayClose = moment.tz(
      `${fridayOfWeek.format('YYYY-MM-DD')} ${businessEnd}`,
      'YYYY-MM-DD HH:mm',
      ADMIN_TIMEZONE
    );

    return {
      dueBy: moment.min(twentyFourHoursLater, fridayClose),
      isAfterHours,
      isBeforeHours,
    };
  }

  // During hours on weekday -> next business day same time
  const dueBy = getNextBusinessDay(submissionTime, businessStart, businessEnd);
  return { dueBy, isAfterHours, isBeforeHours };
}

export function needsFileUpload(sites: Site[]): boolean {
  return sites.some((site) =>
    site.images.some((img) => {
      if (img instanceof File) return true;
      if (typeof img === 'object' && 'status' in img && img.status === 'failed' && img.originalFile) {
        return true;
      }
      return false;
    })
  );
}

export function hasFailedUploads(sites: Site[]): boolean {
  return sites.some((site) =>
    site.images.some((img) => {
      if (typeof img === 'object' && 'status' in img) {
        return (img as any).status === 'failed';
      }
      return false;
    })
  );
}
